/**
 * Firebase Collections API - Complete Implementation
 * All 16 collections from DATABASE_SCHEMA.md
 * Date: November 24, 2025
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  DocumentData,
  QueryConstraint
} from 'firebase/firestore';
import { db } from './client';
import type * as Types from '../../types/database';

// Helper to convert Firestore timestamps to Date
function convertTimestamps(data: DocumentData): any {
  const converted: any = { ...data };
  for (const key in converted) {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toDate();
    } else if (typeof converted[key] === 'object' && converted[key] !== null) {
      converted[key] = convertTimestamps(converted[key]);
    }
  }
  return converted;
}

// Helper to build query
function buildQuery(
  collectionName: string,
  filters?: Array<{ field: string; operator: any; value: any }>,
  orderByField?: string,
  orderDirection: 'asc' | 'desc' = 'desc',
  limitCount?: number
) {
  const collectionRef = collection(db, collectionName);
  const constraints: QueryConstraint[] = [];

  if (filters) {
    filters.forEach(filter => {
      constraints.push(where(filter.field, filter.operator, filter.value));
    });
  }

  if (orderByField) {
    constraints.push(orderBy(orderByField, orderDirection));
  }

  if (limitCount) {
    constraints.push(limit(limitCount));
  }

  return query(collectionRef, ...constraints);
}

// ==================== COMPANIES ====================

export async function getCompanies(): Promise<Types.Company[]> {
  const companiesRef = collection(db, 'companies');
  const snapshot = await getDocs(companiesRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Company[];
}

export async function getCompany(companyId: string): Promise<Types.Company | null> {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as Types.Company : null;
}

export async function createCompany(data: Omit<Types.Company, 'id'>): Promise<string> {
  const companiesRef = collection(db, 'companies');
  const docRef = await addDoc(companiesRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateCompany(companyId: string, data: Partial<Types.Company>): Promise<void> {
  const docRef = doc(db, 'companies', companyId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

export async function deleteCompany(companyId: string): Promise<void> {
  const docRef = doc(db, 'companies', companyId);
  await deleteDoc(docRef);
}

// ==================== WELLS ====================

export async function getWells(companyId: string): Promise<Types.Well[]> {
  const wellsRef = collection(db, `companies/${companyId}/wells`);
  const q = query(wellsRef, where('companyId', '==', companyId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Well[];
}

export async function getWell(companyId: string, wellId: string): Promise<Types.Well | null> {
  const docRef = doc(db, `companies/${companyId}/wells`, wellId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as Types.Well : null;
}

export async function createWell(companyId: string, data: Omit<Types.Well, 'id'>): Promise<string> {
  const wellsRef = collection(db, `companies/${companyId}/wells`);
  const docRef = await addDoc(wellsRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateWell(companyId: string, wellId: string, data: Partial<Types.Well>): Promise<void> {
  const docRef = doc(db, `companies/${companyId}/wells`, wellId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

export async function deleteWell(companyId: string, wellId: string): Promise<void> {
  const docRef = doc(db, `companies/${companyId}/wells`, wellId);
  await deleteDoc(docRef);
}

// Well Subcollections
export async function getWellNotes(companyId: string, wellId: string): Promise<Types.WellNote[]> {
  const notesRef = collection(db, `companies/${companyId}/wells/${wellId}/notes`);
  const snapshot = await getDocs(notesRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.WellNote[];
}

export async function createWellNote(companyId: string, wellId: string, data: Omit<Types.WellNote, 'id'>): Promise<string> {
  const notesRef = collection(db, `companies/${companyId}/wells/${wellId}/notes`);
  const docRef = await addDoc(notesRef, {
    ...data,
    createdAt: Timestamp.now()
  });
  return docRef.id;
}

export async function getWellPhotos(companyId: string, wellId: string): Promise<Types.WellPhoto[]> {
  const photosRef = collection(db, `companies/${companyId}/wells/${wellId}/photos`);
  const snapshot = await getDocs(photosRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.WellPhoto[];
}

export async function createWellPhoto(companyId: string, wellId: string, data: Omit<Types.WellPhoto, 'id'>): Promise<string> {
  const photosRef = collection(db, `companies/${companyId}/wells/${wellId}/photos`);
  const docRef = await addDoc(photosRef, {
    ...data,
    createdAt: Timestamp.now()
  });
  return docRef.id;
}

// ==================== USERS ====================
// Note: Regular users (pumpers, mechanics, etc.) are in companies/{companyId}/users
// SuperAdmin/Manager users may still be in root /users collection for legacy support

export async function getUsers(companyId: string): Promise<Types.User[]> {
  // First try company-scoped collection (preferred)
  try {
    const companyUsersRef = collection(db, 'companies', companyId, 'users');
    const companySnapshot = await getDocs(companyUsersRef);
    if (companySnapshot.docs.length > 0) {
      return companySnapshot.docs.map(doc => ({
        id: doc.id,
        ...convertTimestamps(doc.data())
      })) as Types.User[];
    }
  } catch (e) {
  }
  
  // Fallback to legacy flat collection
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('companyId', '==', companyId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.User[];
}

export async function getUser(userId: string, companyId?: string): Promise<Types.User | null> {
  // Try company-scoped first if companyId provided
  if (companyId) {
    try {
      const companyDocRef = doc(db, 'companies', companyId, 'users', userId);
      const companySnap = await getDoc(companyDocRef);
      if (companySnap.exists()) {
        return { id: companySnap.id, ...convertTimestamps(companySnap.data()) } as Types.User;
      }
    } catch (e) {
    }
  }
  
  // Fallback to legacy flat collection
  const docRef = doc(db, 'users', userId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as Types.User : null;
}

export async function createUser(userId: string, data: Omit<Types.User, 'id'>, companyId?: string): Promise<void> {
  const effectiveCompanyId = companyId || (data as any).companyId;
  
  // Always create in company-scoped collection for non-superadmin users
  if (effectiveCompanyId && (data as any).role !== 'superadmin') {
    const companyDocRef = doc(db, 'companies', effectiveCompanyId, 'users', userId);
    await setDoc(companyDocRef, {
      ...data,
      companyId: effectiveCompanyId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return;
  }
  
  // SuperAdmin or no companyId goes to flat collection
  const docRef = doc(db, 'users', userId);
  await setDoc(docRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
}

export async function updateUser(userId: string, data: Partial<Types.User>, companyId?: string): Promise<void> {
  // Try company-scoped first if companyId provided
  if (companyId) {
    try {
      const companyDocRef = doc(db, 'companies', companyId, 'users', userId);
      const companySnap = await getDoc(companyDocRef);
      if (companySnap.exists()) {
        await updateDoc(companyDocRef, {
          ...data,
          updatedAt: Timestamp.now()
        });
        return;
      }
    } catch (e) {
    }
  }
  
  // Fallback to legacy flat collection
  const docRef = doc(db, 'users', userId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

export async function deleteUser(userId: string, companyId?: string): Promise<void> {
  // Try company-scoped first if companyId provided
  if (companyId) {
    try {
      const companyDocRef = doc(db, 'companies', companyId, 'users', userId);
      const companySnap = await getDoc(companyDocRef);
      if (companySnap.exists()) {
        await deleteDoc(companyDocRef);
        return;
      }
    } catch (e) {
    }
  }
  
  // Fallback to legacy flat collection
  const docRef = doc(db, 'users', userId);
  await deleteDoc(docRef);
}

// ==================== PRODUCTION DATA ====================

export async function getProductionRecords(companyId: string, wellId?: string): Promise<Types.ProductionRecord[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (wellId) filters.push({ field: 'wellId', operator: '==', value: wellId });
  
  const q = buildQuery('production', filters, 'date', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.ProductionRecord[];
}

export async function createProductionRecord(data: Omit<Types.ProductionRecord, 'id'>): Promise<string> {
  const productionRef = collection(db, 'production');
  const docRef = await addDoc(productionRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateProductionRecord(recordId: string, data: Partial<Types.ProductionRecord>): Promise<void> {
  const docRef = doc(db, 'production', recordId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

export async function deleteProductionRecord(recordId: string): Promise<void> {
  const docRef = doc(db, 'production', recordId);
  await deleteDoc(docRef);
}

// ==================== HAULING RECORDS ====================

export async function getHaulingRecords(companyId: string, wellId?: string, type?: 'oil' | 'water'): Promise<Types.HaulingRecord[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (wellId) filters.push({ field: 'wellId', operator: '==', value: wellId });
  if (type) filters.push({ field: 'type', operator: '==', value: type });
  
  const q = buildQuery('haulingRecords', filters, 'haulDate', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.HaulingRecord[];
}

export async function createHaulingRecord(data: Omit<Types.HaulingRecord, 'id'>): Promise<string> {
  const haulingRef = collection(db, 'haulingRecords');
  const docRef = await addDoc(haulingRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateHaulingRecord(recordId: string, data: Partial<Types.HaulingRecord>): Promise<void> {
  const docRef = doc(db, 'haulingRecords', recordId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== SERVICE TICKETS ====================

export async function getServiceTickets(
  companyId: string, 
  status?: Types.TicketStatus,
  wellId?: string
): Promise<Types.ServiceTicket[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (status) filters.push({ field: 'status', operator: '==', value: status });
  if (wellId) filters.push({ field: 'wellId', operator: '==', value: wellId });
  
  const q = buildQuery('serviceTickets', filters, 'createdAt', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.ServiceTicket[];
}

export async function getServiceTicket(ticketId: string): Promise<Types.ServiceTicket | null> {
  const docRef = doc(db, 'serviceTickets', ticketId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as Types.ServiceTicket : null;
}

export async function createServiceTicket(data: Omit<Types.ServiceTicket, 'id'>): Promise<string> {
  const ticketsRef = collection(db, 'serviceTickets');
  const docRef = await addDoc(ticketsRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateServiceTicket(ticketId: string, data: Partial<Types.ServiceTicket>): Promise<void> {
  const docRef = doc(db, 'serviceTickets', ticketId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

export async function addTicketUpdate(ticketId: string, update: Types.TicketUpdate): Promise<void> {
  const ticket = await getServiceTicket(ticketId);
  if (!ticket) throw new Error('Ticket not found');
  
  const updates = [...(ticket.updates || []), { ...update, createdAt: new Date() }];
  await updateServiceTicket(ticketId, { updates });
}

// ==================== VENDORS ====================

export async function getVendors(companyId: string, serviceType?: Types.ServiceType): Promise<Types.Vendor[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (serviceType) filters.push({ field: 'services', operator: 'array-contains', value: serviceType });
  
  const q = buildQuery('vendors', filters, 'companyName', 'asc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Vendor[];
}

export async function getVendor(vendorId: string): Promise<Types.Vendor | null> {
  const docRef = doc(db, 'vendors', vendorId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...convertTimestamps(docSnap.data()) } as Types.Vendor : null;
}

export async function createVendor(data: Omit<Types.Vendor, 'id'>): Promise<string> {
  const vendorsRef = collection(db, 'vendors');
  const docRef = await addDoc(vendorsRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateVendor(vendorId: string, data: Partial<Types.Vendor>): Promise<void> {
  const docRef = doc(db, 'vendors', vendorId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== INVOICES ====================

export async function getInvoices(companyId: string, status?: string): Promise<Types.Invoice[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (status) filters.push({ field: 'status', operator: '==', value: status });
  
  const q = buildQuery('invoices', filters, 'issueDate', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Invoice[];
}

export async function createInvoice(data: Omit<Types.Invoice, 'id'>): Promise<string> {
  const invoicesRef = collection(db, 'invoices');
  const docRef = await addDoc(invoicesRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateInvoice(invoiceId: string, data: Partial<Types.Invoice>): Promise<void> {
  const docRef = doc(db, 'invoices', invoiceId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== EQUIPMENT ====================

export async function getEquipment(companyId: string, type?: string): Promise<Types.Equipment[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (type) filters.push({ field: 'type', operator: '==', value: type });
  
  const q = buildQuery('equipment', filters, 'name', 'asc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Equipment[];
}

export async function createEquipment(data: Omit<Types.Equipment, 'id'>): Promise<string> {
  const equipmentRef = collection(db, 'equipment');
  const docRef = await addDoc(equipmentRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateEquipment(equipmentId: string, data: Partial<Types.Equipment>): Promise<void> {
  const docRef = doc(db, 'equipment', equipmentId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== PARTS INVENTORY ====================

export async function getParts(companyId: string, category?: string): Promise<Types.Part[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (category) filters.push({ field: 'category', operator: '==', value: category });
  
  const q = buildQuery('parts', filters, 'name', 'asc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Part[];
}

export async function createPart(data: Omit<Types.Part, 'id'>): Promise<string> {
  const partsRef = collection(db, 'parts');
  const docRef = await addDoc(partsRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updatePart(partId: string, data: Partial<Types.Part>): Promise<void> {
  const docRef = doc(db, 'parts', partId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== TASKS ====================

export async function getTasks(
  companyId: string, 
  assignedTo?: string,
  status?: string
): Promise<Types.Task[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (assignedTo) filters.push({ field: 'assignedTo', operator: '==', value: assignedTo });
  if (status) filters.push({ field: 'status', operator: '==', value: status });
  
  const q = buildQuery('tasks', filters, 'createdAt', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Task[];
}

export async function createTask(data: Omit<Types.Task, 'id'>): Promise<string> {
  const tasksRef = collection(db, 'tasks');
  const docRef = await addDoc(tasksRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    lastUpdated: Timestamp.now()
  });
  return docRef.id;
}

export async function updateTask(taskId: string, data: Partial<Types.Task>): Promise<void> {
  const docRef = doc(db, 'tasks', taskId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
    lastUpdated: Timestamp.now()
  });
}

// ==================== NOTIFICATIONS ====================

export async function getNotifications(companyId: string, userId?: string): Promise<Types.Notification[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (userId) filters.push({ field: 'userId', operator: '==', value: userId });
  
  const q = buildQuery('notifications', filters, 'createdAt', 'desc', 50);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Notification[];
}

export async function createNotification(data: Omit<Types.Notification, 'id'>): Promise<string> {
  const notificationsRef = collection(db, 'notifications');
  const docRef = await addDoc(notificationsRef, {
    ...data,
    createdAt: Timestamp.now()
  });
  return docRef.id;
}

export async function markNotificationAsRead(notificationId: string): Promise<void> {
  const docRef = doc(db, 'notifications', notificationId);
  await updateDoc(docRef, {
    read: true,
    readAt: Timestamp.now()
  });
}

// ==================== TIME CLOCK ====================

export async function getTimeClockEntries(companyId: string, userId?: string, date?: Date): Promise<Types.TimeClockEntry[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (userId) filters.push({ field: 'userId', operator: '==', value: userId });
  if (date) filters.push({ field: 'date', operator: '==', value: Timestamp.fromDate(date) });
  
  const q = buildQuery('timeClock', filters, 'clockInTime', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.TimeClockEntry[];
}

export async function createTimeClockEntry(data: Omit<Types.TimeClockEntry, 'id'>): Promise<string> {
  const timeClockRef = collection(db, 'timeClock');
  const docRef = await addDoc(timeClockRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateTimeClockEntry(entryId: string, data: Partial<Types.TimeClockEntry>): Promise<void> {
  const docRef = doc(db, 'timeClock', entryId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== ROUTES ====================

export async function getRoutes(companyId: string, assignedTo?: string): Promise<Types.Route[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (assignedTo) filters.push({ field: 'assignedTo', operator: '==', value: assignedTo });
  
  const q = buildQuery('routes', filters, 'createdAt', 'desc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Route[];
}

export async function createRoute(data: Omit<Types.Route, 'id'>): Promise<string> {
  const routesRef = collection(db, 'routes');
  const docRef = await addDoc(routesRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateRoute(routeId: string, data: Partial<Types.Route>): Promise<void> {
  const docRef = doc(db, 'routes', routeId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

// ==================== MESSAGES ====================

export async function getMessages(companyId: string, userId?: string, threadId?: string): Promise<Types.Message[]> {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (threadId) filters.push({ field: 'threadId', operator: '==', value: threadId });
  
  const q = buildQuery('messages', filters, 'createdAt', 'desc', 100);
  const snapshot = await getDocs(q);
  const messages = snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Message[];
  
  // Filter by userId if provided (check if user is in recipients)
  if (userId) {
    return messages.filter(msg => 
      msg.senderId === userId || 
      msg.recipients.some(r => r.userId === userId)
    );
  }
  
  return messages;
}

export async function createMessage(data: Omit<Types.Message, 'id'>): Promise<string> {
  const messagesRef = collection(db, 'messages');
  const docRef = await addDoc(messagesRef, {
    ...data,
    createdAt: Timestamp.now()
  });
  return docRef.id;
}

// ==================== COMMENTS ====================

export async function getComments(companyId: string, targetType: string, targetId: string): Promise<Types.Comment[]> {
  const filters = [
    { field: 'companyId', operator: '==', value: companyId },
    { field: 'targetType', operator: '==', value: targetType },
    { field: 'targetId', operator: '==', value: targetId }
  ];
  
  const q = buildQuery('comments', filters, 'createdAt', 'asc');
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...convertTimestamps(doc.data())
  })) as Types.Comment[];
}

export async function createComment(data: Omit<Types.Comment, 'id'>): Promise<string> {
  const commentsRef = collection(db, 'comments');
  const docRef = await addDoc(commentsRef, {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
}

export async function updateComment(commentId: string, data: Partial<Types.Comment>): Promise<void> {
  const docRef = doc(db, 'comments', commentId);
  await updateDoc(docRef, {
    ...data,
    edited: true,
    editedAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
}
